"use client"

/**
 * BankEmptyState — the one empty-state shape for the Add questions flow.
 *
 * The package has no `EmptyState` primitive; the DS slots (`HubTable.emptyState`,
 * `DataRowList.emptyState`) take any node, and every hub had been passing a bare
 * sentence. That is fine when there is nothing to do about it, and wrong here:
 * an empty bank, an empty folder, a search that matched nothing, and "everything
 * is already added" each have a different way out, and the author cannot guess
 * which one they are in from identical grey text.
 *
 * One sentence, at most two actions (M8). The icon is decorative — the title
 * carries the meaning, so it stays `aria-hidden` (§8.6 Case A).
 */

import * as React from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface BankEmptyStateAction {
  label: string
  onClick: () => void
  icon?: string
  variant?: "default" | "outline" | "ghost"
}

export interface BankEmptyStateProps {
  /** Font Awesome glyph, decorative. */
  icon?: string
  title: string
  /** One sentence. Longer copy belongs in help, not in an empty state. */
  description?: React.ReactNode
  /** Primary first. Two is the ceiling — a third reads as a menu. */
  actions?: BankEmptyStateAction[]
  /**
   * `panel` trims the padding for the 300px side rail. `hero` is for the whole
   * page or pane standing in for the surface — nothing else is on screen, so
   * the block carries the weight the table would have.
   */
  density?: "page" | "panel" | "hero"
  /**
   * `table` pins the block to the left edge of the scroll port. The pool grid is
   * wider than its pane, so a centred block centres on the *table* and half of
   * it sits past the fold — measured in Chrome, the second action was cut off.
   */
  layout?: "center" | "table"
  className?: string
}

export function BankEmptyState({
  icon = "fa-folder-open",
  title,
  description,
  actions = [],
  density = "page",
  layout = "center",
  className,
}: BankEmptyStateProps) {
  const panel = density === "panel"
  const hero = density === "hero"
  const table = layout === "table"

  return (
    <div
      className={cn(
        "flex flex-col justify-center gap-2",
        table
          ? "sticky start-0 w-fit max-w-md items-start px-4 py-10 text-start"
          : "items-center text-center",
        !table && (panel ? "px-2 py-8" : "px-6 py-14"),
        // Fills whatever it is handed so the block lands optically centred
        // rather than pinned under the header.
        hero && "min-h-0 flex-1 gap-3",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex items-center justify-center rounded-full bg-muted text-muted-foreground",
          panel ? "size-8 text-sm" : "size-11 text-lg",
          hero && "size-14 text-2xl",
        )}
      >
        <i className={cn("fa-light", icon)} aria-hidden />
      </span>
      <p
        className={cn(
          "font-medium text-foreground",
          panel ? "text-sm" : "text-base",
          hero && "text-lg",
        )}
      >
        {title}
      </p>
      {description ? (
        <p className="max-w-md text-sm text-muted-foreground">{description}</p>
      ) : null}
      {actions.length > 0 ? (
        <div
          className={cn(
            "mt-2 flex flex-wrap items-center gap-2",
            table ? "justify-start" : "justify-center",
          )}
        >
          {actions.map((action, index) => (
            <Button
              key={action.label}
              type="button"
              size={hero ? "lg" : "sm"}
              variant={action.variant ?? (index === 0 ? "default" : "outline")}
              onClick={action.onClick}
            >
              {action.icon ? (
                <i className={cn("fa-light", action.icon)} aria-hidden />
              ) : null}
              {action.label}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
